import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Flame, Target, Zap, Clock, ArrowRight } from "lucide-react";

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/stats/me").then((r) => setStats(r.data)).catch(() => setStats(null));
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 space-y-8" data-testid="dashboard-container">
            <div data-testid="dashboard-welcome">
                <p className="eyebrow">Welcome back</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-2">
                    Namaste, <span className="text-[var(--lt-terracotta)]">{user?.name || "friend"}</span>
                </h1>
                <p className="text-[var(--lt-muted)] mt-3">
                    Keep your streak alive and beat your best.
                </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric
                    icon={<Flame className="text-[var(--lt-amber)]" />}
                    label="Streak"
                    value={`${stats?.streak_days ?? 0} days`}
                    testid="dashboard-streak-metric"
                />
                <Metric
                    icon={<Zap className="text-[var(--lt-terracotta)]" />}
                    label="Best WPM (EN)"
                    value={stats?.best_wpm_english ?? 0}
                    testid="dashboard-wpm-metric"
                />
                <Metric
                    icon={<Zap className="text-[var(--lt-cyan)]" />}
                    label="Best WPM (ने)"
                    value={stats?.best_wpm_nepali ?? 0}
                    testid="dashboard-wpm-nepali-metric"
                />
                <Metric
                    icon={<Target className="text-[var(--lt-emerald)]" />}
                    label="Avg Accuracy"
                    value={`${stats?.avg_accuracy ?? 0}%`}
                    testid="dashboard-accuracy-metric"
                />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <Link
                    to="/lessons"
                    className="glass rounded-2xl p-8 group hover:border-[var(--lt-terracotta)] transition-colors"
                    data-testid="dashboard-start-lesson-btn"
                >
                    <p className="eyebrow">Continue Learning</p>
                    <h3 className="font-heading text-2xl font-bold mt-2">Structured Lessons</h3>
                    <p className="text-[var(--lt-muted)] mt-2 text-sm">
                        Home row · Top row · Bottom row · Paragraphs · Devanagari
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[var(--lt-terracotta)] font-semibold">
                        Open lessons <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>
                <Link
                    to="/test"
                    className="glass rounded-2xl p-8 group hover:border-[var(--lt-terracotta)] transition-colors"
                    data-testid="dashboard-quick-test-btn"
                >
                    <p className="eyebrow">Challenge Yourself</p>
                    <h3 className="font-heading text-2xl font-bold mt-2">Take a Timed Test</h3>
                    <p className="text-[var(--lt-muted)] mt-2 text-sm">
                        30s · 60s · 120s speed runs with instant analysis.
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[var(--lt-terracotta)] font-semibold">
                        Start test <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>
            </div>

            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="eyebrow">Recent Runs</p>
                        <h3 className="font-heading text-2xl font-bold mt-1">History</h3>
                    </div>
                    <Clock className="text-[var(--lt-muted)]" />
                </div>
                {!stats?.recent?.length ? (
                    <p className="text-sm text-[var(--lt-muted)]">
                        No runs yet. Start a practice or test to see stats appear here.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {stats.recent.map((r) => (
                            <div
                                key={r.id}
                                className="flex items-center justify-between py-3 border-b border-[var(--lt-border)] last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="eyebrow">{r.mode}</span>
                                    <span className="text-sm">
                                        {r.language === "nepali" ? "नेपाली" : "English"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-6 text-sm">
                                    <span className="font-mono-stat">{r.wpm.toFixed(0)} wpm</span>
                                    <span className="text-[var(--lt-muted)] font-mono-stat">
                                        {r.accuracy.toFixed(0)}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Metric({ icon, label, value, testid }) {
    return (
        <div className="glass rounded-2xl p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="eyebrow">{label}</span>
                {icon}
            </div>
            <div className="font-mono-stat text-3xl mt-3">{value}</div>
        </div>
    );
}
